import { MAX_CONTENT_COLUMNS } from "@/lib/cms-puck/blocks/column-count";
import { createColumnCountField } from "@/components/cms-puck/fields/ColumnCountSliderField";
import { PageAccessField } from "@/components/cms-puck/fields/PageAccessField";
import { PageAppearanceField } from "@/components/cms-puck/fields/PageAppearanceField";
import { PageCustomCssField } from "@/components/cms-puck/fields/PageCustomCssField";
import { PagePaddingField } from "@/components/cms-puck/fields/PagePaddingField";
import { DEFAULT_PUCK_ACCESS_LEVEL, normalizePuckAccessLevel } from "@/lib/cms-puck/blocks/page-access";
import { type Config } from "@measured/puck";
import { DEFAULT_PAGE_APPEARANCE } from "@/lib/cms-puck/blocks/appearance";
import { DEFAULT_PAGE_PADDING } from "@/lib/cms-puck/blocks/page-padding";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import { RootPreview } from "@/components/cms-puck/blocks/RootPreview";
import { type PuckComponents, type PuckRootProps } from "./types";
import { applyStyleToAll } from "./assembly";
import { buildSectionsBlocks } from "./blocks/sections";
import { buildLayoutBlocks } from "./blocks/layout";
import { buildContentBlocks } from "./blocks/content";
import { buildArtworksBlocks } from "./blocks/artworks";
import { buildPaymentBlocks } from "./blocks/payment";
import { buildMemberBlocks } from "./blocks/member";
import { buildShellBlocks } from "./blocks/shell";
import { buildCatalogueBlocks } from "./blocks/catalogue";

/**
 * Config Puck = LA source de vérité des composants. Ajouter un bloc ici = du code,
 * jamais de migration (cf. principe #1). La BDD ne connaît jamais ces types : elle
 * stocke le doc Puck en Json opaque.
 *
 * Props traduisibles (title, body…) : éditées en clair ici pour le WYSIWYG ;
 * extraites vers `ContentString` au save côté backend (R1/R3). Props structurelles
 * (variant, columns, artworkIds…) : restent dans le layout, identiques toutes langues.
 *
 * Apparence PAR PAGE : stockée dans `root.props.appearance` (structurel, Json
 * opaque côté API). Éditée via un custom field réutilisant les composants de
 * l'ancien CMS ; aperçu live appliqué par `root.render`.
 */

export function createPuckConfig(opts?: {
  /** Label du root dans la sidebar Puck (« Page » / « Modèle »). */
  rootLabel?: string;
}): Config<PuckComponents, PuckRootProps> {
  const rootLabel =
    opts?.rootLabel ?? cmsNew("cms.new.auto.text.page", "Page");

  const contentColumnCountField = createColumnCountField<2 | 3 | 4>(
    cmsNew("cms.new.fields.columnCount.colonnes", "Colonnes"),
    1,
    MAX_CONTENT_COLUMNS,
  );
  const galleryColumnCountField = createColumnCountField<number>(
    cmsNew("cms.new.fields.columnCount.colonnes", "Colonnes"),
    1,
    MAX_CONTENT_COLUMNS,
  );
  const newsColumnCountField = createColumnCountField<2 | 3>(
    cmsNew("cms.new.fields.columnCount.colonnes", "Colonnes"),
    2,
    3,
  );
  const columnsLayoutCountField = createColumnCountField<2 | 3 | 4>(
    cmsNew(
      "cms.new.fields.columnCount.nombre_de_colonnes",
      "Nombre de colonnes",
    ),
    2,
    4,
  );
  const gridColumnCountField = createColumnCountField(
    cmsNew("cms.new.fields.columnCount.colonnes_1_7", "Colonnes (1–7)"),
    1,
    MAX_CONTENT_COLUMNS,
  );

  return {
    // Regroupe les blocs dans la left sidebar de l'éditeur.
    categories: {
      sections: {
        title: cmsNew("cms.new.categories.sections", "Sections"),
        components: [
          "TextImage",
          "Cta",
          "Banner",
          "Accordion",
          "Tabs",
          "ContactForm",
        ],
      },
      layout: {
        title: cmsNew("cms.new.categories.layout", "Mise en page"),
        components: ["Container", "Columns", "Grid", "Spacer"],
      },
      content: {
        title: cmsNew("cms.new.categories.content", "Contenu"),
        components: [
          "Heading",
          "RichText",
          "Button",
          "Download",
          "Quote",
          "Icon",
          "Divider",
          "Media",
          "Gallery",
          "Carousel",
          "SocialLinks",
          "LogoGrid",
          "Testimonials",
          "MapEmbed",
          "VideoHero",
          "PdfReader",
          "Html",
          "News",
        ],
      },
      artworks: {
        title: cmsNew("cms.new.categories.artworks", "Œuvres"),
        components: [
          "SelectionSections",
          "SelectionCategoryTitle",
          "SelectionCategoryDescription",
          "SelectionCategoryDetail",
          "ArtworkSelection",
          "ItemSpotlight",
          "ExhibitionList",
        ],
      },
      payment: {
        title: cmsNew("cms.new.categories.payment", "Paiement"),
        components: [
          "PaymentButton",
          "ExpertiseOffers",
          "SubscriptionOffers",
          "EshopListing",
        ],
      },
      memberSpace: {
        title: cmsNew("cms.new.categories.memberSpace", "Espace membre"),
        components: [
          "MemberLogin",
          "MemberSignup",
          "MemberProfile",
          "MemberFavorites",
        ],
      },
      shell: {
        title: cmsNew("cms.new.categories.shell", "En-tête & pied de page"),
        components: ["SiteMenu", "HeaderUserAuth", "HeaderBasket", "HeaderLanguage"],
      },
      navigation: {
        title: cmsNew("cms.new.categories.navigation", "Navigation"),
        components: ["Breadcrumb"],
      },
      catalogueRaisonne: {
        title: cmsNew(
          "cms.new.categories.catalogueRaisonne",
          "Catalogue raisonné",
        ),
        components: [
          "CatalogueCtaButton",
          "CatalogueDecoupages",
          "CatalogueDecoupage",
          "CatalogueFilters",
          "CatalogueSearchResults",
          "CatalogueListing",
          "CatalogueItemDetail",
          // Remplacés par des blocs génériques (Titre H1/H2 + Bouton) : convertis
          // à l'ouverture de l'en-tête, gardés pour ne pas perdre un layout hérité.
          "SiteLogo",
          "HeaderCta",
          // Remplacé par des colonnes de blocs génériques (cf. SiteLogo).
          "FooterContent",
        ],
      },
    },
    root: {
      label: rootLabel,
      fields: {
        appearance: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.apparence_de_la_page",
            "Apparence de la page",
          ),
          render: ({ value, onChange }) => (
            <PageAppearanceField value={value} onChange={onChange} />
          ),
        },
        accessLevel: {
          type: "custom",
          label: cmsNew("cms.new.auto.label.acces", "Accès"),
          render: ({ value, onChange }) => (
            <PageAccessField
              value={normalizePuckAccessLevel(value)}
              onChange={onChange}
            />
          ),
        },
        customCss: {
          type: "custom",
          label: cmsNew(
            "cms.new.auto.label.css_personnalise",
            "CSS personnalisé",
          ),
          render: ({ value, onChange }) => (
            <PageCustomCssField value={value} onChange={onChange} />
          ),
        },
        padding: {
          type: "custom",
          label: cmsNew(
            "cms.new.page.settings.paddingTitle",
            "Marge intérieure de la page",
          ),
          render: ({ value, onChange }) => (
            <PagePaddingField value={value} onChange={onChange} />
          ),
        },
      },
      defaultProps: {
        appearance: DEFAULT_PAGE_APPEARANCE,
        accessLevel: DEFAULT_PUCK_ACCESS_LEVEL,
        padding: DEFAULT_PAGE_PADDING,
        customCss: "",
      },
      render: ({ children, appearance, padding, customCss }) => (
        <RootPreview
          appearance={appearance}
          padding={padding}
          customCss={customCss}
        >
          {children}
        </RootPreview>
      ),
    },
    components: applyStyleToAll({
      ...buildSectionsBlocks(),
      ...buildLayoutBlocks({ columnsLayoutCountField, gridColumnCountField }),
      ...buildContentBlocks({ galleryColumnCountField, newsColumnCountField }),
      ...buildArtworksBlocks({ contentColumnCountField }),
      ...buildPaymentBlocks(),
      ...buildMemberBlocks(),
      ...buildShellBlocks(),
      ...buildCatalogueBlocks({ contentColumnCountField }),
    }),
  };
}
